/*
 * Presentation Templates — its own, independent fetch/parse pipeline.
 * Deliberately NOT shared with slide-templates/services/slideTemplatesApi.ts:
 * Slide Templates is kept as a separate, self-contained feature and is only
 * ever used here as a reference for the pattern, never imported from. Every
 * type and helper below is this feature's own copy, except the genuinely
 * generic directory-SAS URL rebuild (`directorySasFileUrl`), which both
 * features' manifest fetches need byte-for-byte identically and which
 * therefore lives in shared/services/blobImageUpload.ts instead.
 *
 * ONE KNOWN MANIFEST FILE — SAME PATTERN AS SLIDE TEMPLATES
 * The presentation folder holds per-file `{Name}.pptx` / `{Name}.cache`
 * pairs, but ALSO one aggregate `presentations.cache` file that already
 * combines every presentation's slide entries — `{SlideNumber, SlideName,
 * FilePath, Category?, Base64ImageData}` per slide, `FilePath` naming which
 * source `.pptx` it belongs to (e.g. "templates\presentations\Board_Meeting.pptx").
 * Since its filename is known in advance, this fetches it exactly the way
 * Slide Templates fetches its own single "standard-layouts.signatures.json"
 * manifest — plain Read permission on the directory SAS is enough; no List
 * permission, no folder enumeration, no backend change required.
 *
 * REAL THUMBNAILS, NO PPTX NEEDED FOR THE GRID
 * Unlike the per-file `.cache` entries (legacy, image-less — see
 * DeckorateTemplatePreprocessor's `load_legacy_cache`), this aggregate file's
 * `Base64ImageData` is an actual rendered slide thumbnail (PNG), so every
 * card gets a real preview straight from this one download.
 *
 * PPTX IS ONLY TOUCHED ON SELECT/INSERT
 * A slide's `sourceFileUrl` (its `FilePath`'s basename, resolved against the
 * same directory SAS) is enough to fetch + carve out that one slide at
 * insert time — see services/presentationTemplateInsert.ts — which needed no
 * changes: it already worked off `sourceFileUrl` + `slideIndex` alone.
 *
 * TWO-LEVEL SHAPE
 * All slides parsed with the same `FilePath` share one `sourceFileUrl`, so
 * `groupIntoPresentations` (unchanged) clusters them into one
 * `PresentationTemplate` per source `.pptx`.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import { directorySasFileUrl, sasVersionHeaders } from "../../shared/services/blobImageUpload";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import {
  clampPct,
  roundPct,
  toPreviewRole,
  type SlidePreview,
  type SlidePreviewBlock,
} from "../../shared/services/slidePreviewGeometry";
import { logError, scrubMessage } from "../../../../../telemetry";

const PRESENTATION_TEMPLATES_CONNECTOR = "deckorate";
const BLOB_SAS_PATH = "/v1/deckorate/content/blob-sas";

/*
 * NOTE: client_id is hardcoded for now — this endpoint doesn't yet
 * distinguish tenants on this call path. Once the real Studio client id
 * (e.g. useOptionalAppConfig()?.data?.client.id) is available, replace this
 * constant with the real value instead of the literal below.
 */
const PRESENTATION_TEMPLATES_CLIENT_ID = 1;
const PRESENTATION_TEMPLATES_TRANSACTION_TYPE = "TemplateTransaction";
const PRESENTATION_TEMPLATES_TEMPLATE_TYPE = "Presentation";

const DIRECTORY_REQUEST_FAILED_MESSAGE =
  "Couldn't find the presentation template library — the blob-sas request didn't return a usable asset.";
const CACHE_DOWNLOAD_FAILED_MESSAGE = "Downloading the presentation template library failed.";
const CACHE_NOT_JSON_MESSAGE = "The presentation template library response wasn't valid JSON.";

/** Known, fixed filename — a sibling of every per-presentation `.pptx` under
 *  the same directory SAS. See the module header for why this file, alone,
 *  removes the need for List permission entirely. */
const AGGREGATE_CACHE_FILE_NAME = "presentations.cache";

export function logPresentationTemplatesError(reason: string, message: string): void {
  logError(
    "error.connector.failure",
    { type: "PresentationTemplatesError", scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: PRESENTATION_TEMPLATES_CONNECTOR, reason },
  );
}

interface BlobSasRequest {
  client_id: number;
  transaction_type: typeof PRESENTATION_TEMPLATES_TRANSACTION_TYPE;
  template_type: typeof PRESENTATION_TEMPLATES_TEMPLATE_TYPE;
}

interface RawBlobSasAsset {
  sas_url?: unknown;
  /** Container-relative folder path — see `requestDirectorySasInfo` for why
   *  this can name a DIFFERENT (deeper) folder than the SAS token's own
   *  signed directory scope, and why that's fine to use anyway. */
  blob_path?: unknown;
}

interface RawBlobSasResponse {
  assets?: unknown;
}

/**
 * Rebuild a fetchable URL for an ABSOLUTE container-relative path, ignoring
 * the SAS URL's own path entirely (only its host/query string — the actual
 * auth — is reused). Needed because the blob-sas response's own `blob_path`
 * can point at a folder one level deeper than the SAS token's signed
 * directory scope (a known backend seed-data quirk this feature works around
 * without needing a backend fix — see `requestDirectorySasInfo`). This is
 * still safe to fetch: a directory-scoped SAS's Read permission is recursive
 * to every descendant blob under the signed directory, not limited to blobs
 * that sit exactly at its own level.
 */
function directorySasAbsolutePathUrl(
  directorySasUrl: string,
  containerRelativePath: string,
  onParseError: (message: string) => void,
): string {
  try {
    const url = new URL(directorySasUrl);
    const container = url.pathname.split("/").find(Boolean);
    if (!container) return directorySasUrl;
    const encodedSegments = containerRelativePath
      .split("/")
      .filter(Boolean)
      .map((segment) => encodeURIComponent(segment));
    url.pathname = `/${encodeURIComponent(container)}/${encodedSegments.join("/")}`;
    return url.toString();
  } catch (error) {
    onParseError((error as TypeError).message);
    return directorySasUrl;
  }
}

/** The placeholder roles a stored preview may carry, plus a catch-all. Kept
 *  only so `toValidPresentationSlidePreview` can still re-validate a preview
 *  written by an older build's manifest-based hand-off payload. Re-exports
 *  the genuinely-shared geometry shape from shared/services/
 *  slidePreviewGeometry.ts under this feature's own names — see that
 *  module's header for why only the leaf geometry is shared, not the
 *  per-feature normalisation around it. */
export type { SlidePreviewRole as PresentationSlidePreviewRole } from "../../shared/services/slidePreviewGeometry";
export type {
  SlidePreviewBlock as PresentationSlidePreviewBlock,
  SlidePreview as PresentationSlidePreview,
};

/** One slide within a presentation template — same field shape as Slide
 *  Templates' own per-slide type, kept as an independent definition. */
export interface PresentationTemplateSlide {
  readonly id: string;
  readonly name: string;
  readonly category: string | null;
  /** Layout family — no `.cache` entry carries one, always null today; kept
   *  so a future richer source only needs to fill this in. */
  readonly layoutClass: string | null;
  readonly description: string | null;
  readonly thumbnailUrl: string | null;
  /** Always null from `.cache` data (no placeholder geometry); kept for the
   *  panel's wireframe-fallback rendering and the hand-off payload shape. */
  readonly preview: SlidePreview | null;
  /** Whether this entry has enough information (`sourceFileUrl` + `slideIndex`)
   *  to be fetched and inserted. */
  readonly insertable: boolean;
  /** Directory-SAS URL for this entry's own source `.pptx` blob (a sibling of
   *  the `.cache` file it came from). Multi-slide: the file this points at is
   *  the WHOLE source deck, not just this entry's slide; `slideIndex` says
   *  which one to carve out at insert time. */
  readonly sourceFileUrl: string | null;
  /** 1-based position of this entry's slide within the file at `sourceFileUrl`. */
  readonly slideIndex: number | null;
}

/** A whole presentation: a group of slides that share the same source .pptx. */
export interface PresentationTemplate {
  readonly id: string;
  readonly name: string;
  /** Most common non-null category among the presentation's slides, ties
   *  broken by first-seen order. */
  readonly category: string | null;
  /** The first slide's thumbnail — the representative preview for the card. */
  readonly thumbnailUrl: string | null;
  readonly slides: readonly PresentationTemplateSlide[];
}

function firstString(...candidates: unknown[]): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate;
  }
  return null;
}

function firstNumber(...candidates: unknown[]): number | null {
  for (const candidate of candidates) {
    if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
  }
  return null;
}

/** 16:9 — what every deck in this library uses; only a guard against bad dims. */
const DEFAULT_SLIDE_ASPECT = 16 / 9;
const MIN_SLIDE_ASPECT = 0.5;
const MAX_SLIDE_ASPECT = 4;

/** Cap on wireframe rectangles per card — the whole set has to fit in
 *  localStorage for the dialog hand-off. */
const MAX_PREVIEW_BLOCKS = 24;
/** Rectangles smaller than this (percent of a side) don't survive downscaling. */
const MIN_BLOCK_SIZE_PCT = 1;

/**
 * Re-validates a preview that came back from storage (the dialog hand-off
 * round-trips through localStorage, which can outlive the build that wrote
 * it, and may still hold a payload written before this feature switched to
 * `.cache`-only data, where `preview` is always null). Bad blocks are
 * dropped, not fixed.
 */
export function toValidPresentationSlidePreview(raw: unknown): SlidePreview | null {
  if (typeof raw !== "object" || raw === null) return null;
  const preview = raw as Record<string, unknown>;

  const rawAspect = firstNumber(preview.aspect);
  const aspect =
    rawAspect !== null && rawAspect >= MIN_SLIDE_ASPECT && rawAspect <= MAX_SLIDE_ASPECT
      ? rawAspect
      : DEFAULT_SLIDE_ASPECT;

  if (!Array.isArray(preview.blocks)) return null;
  const blocks: SlidePreviewBlock[] = [];
  for (const rawBlock of preview.blocks.slice(0, MAX_PREVIEW_BLOCKS)) {
    if (typeof rawBlock !== "object" || rawBlock === null) continue;
    const block = rawBlock as Record<string, unknown>;
    const rawX = firstNumber(block.x);
    const rawY = firstNumber(block.y);
    const rawW = firstNumber(block.w);
    const rawH = firstNumber(block.h);
    if (rawX === null || rawY === null || rawW === null || rawH === null) continue;

    const x = clampPct(rawX);
    const y = clampPct(rawY);
    const w = Math.min(rawW, 100 - x);
    const h = Math.min(rawH, 100 - y);
    if (w < MIN_BLOCK_SIZE_PCT || h < MIN_BLOCK_SIZE_PCT) continue;

    blocks.push({
      role: toPreviewRole(block.role),
      x: roundPct(x),
      y: roundPct(y),
      w: roundPct(w),
      h: roundPct(h),
    });
  }
  return blocks.length > 0 ? { aspect, blocks } : null;
}

/** "Bio_Layout.pptx" / "Bio_Layout" → "Bio Layout". */
function humaniseSlideLabel(raw: string): string {
  return raw
    .replace(/\.pptx$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Handles both `/`-separated blob paths and the `\`-separated Windows-style
 *  paths this manifest's own `FilePath` field uses (e.g.
 *  "templates\\presentations\\Board_Meeting.pptx"). */
function basename(path: string): string {
  const segments = path.split(/[/\\]/);
  for (let i = segments.length - 1; i >= 0; i--) {
    if (segments[i]) return segments[i]!;
  }
  return path;
}

/** Sniffs an image format from its base64 payload's leading bytes — this
 *  manifest carries no explicit mime/extension field alongside
 *  `Base64ImageData`. Every sample seen so far is PNG (`iVBORw0KGgo...`,
 *  the standard PNG signature base64-encoded), kept as the fallback for a
 *  format this doesn't recognise rather than dropping the image outright. */
function sniffImageMimeType(base64: string): string {
  if (base64.startsWith("/9j/")) return "image/jpeg";
  if (base64.startsWith("R0lGOD")) return "image/gif";
  if (base64.startsWith("Qk")) return "image/bmp";
  return "image/png";
}

/** One entry in the aggregate `presentations.cache` manifest — see the
 *  module header. */
interface RawAggregateCacheEntry {
  FilePath?: unknown;
  SlideNumber?: unknown;
  SlideName?: unknown;
  Category?: unknown;
  Base64ImageData?: unknown;
}

function normaliseAggregateCacheEntry(
  raw: unknown,
  arrayIndex: number,
  resolveSourceFileUrl: (pptxFile: string) => string,
): PresentationTemplateSlide | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as RawAggregateCacheEntry;

  const filePath = firstString(entry.FilePath);
  if (!filePath) return null;
  const pptxFile = basename(filePath);

  // Entries are written 1-based already; falling back to the array position
  // only covers an entry that omits the field entirely.
  const slideIndex = firstNumber(entry.SlideNumber) ?? arrayIndex + 1;
  const slideName = firstString(entry.SlideName);
  const category = firstString(entry.Category);
  const name = slideName ?? `${humaniseSlideLabel(pptxFile)} — Slide ${slideIndex}`;

  const imageBase64 = firstString(entry.Base64ImageData);
  const thumbnailUrl = imageBase64 ? `data:${sniffImageMimeType(imageBase64)};base64,${imageBase64}` : null;

  return {
    id: `${pptxFile}#${slideIndex}`,
    name,
    category,
    layoutClass: null,
    description: null,
    thumbnailUrl,
    preview: null,
    insertable: true,
    sourceFileUrl: resolveSourceFileUrl(pptxFile),
    slideIndex,
  };
}

/** "standalone:" prefix means an entry with no derivable source file — never
 *  happens for `.cache`-sourced slides (every one has a pptxFile), kept only
 *  so `groupIntoPresentations` stays a total function. */
function deriveNameFromSourceUrl(sourceFileUrl: string): string | null {
  try {
    const url = new URL(sourceFileUrl);
    const fileName = basename(decodeURIComponent(url.pathname));
    const humanised = humaniseSlideLabel(fileName);
    return humanised || null;
  } catch {
    // Not a real URL (e.g. a malformed/stripped one round-tripped through the
    // dialog hand-off) — fall back to the first slide's own name instead.
    return null;
  }
}

function deriveGroupName(sourceFileUrl: string | null, firstSlide: PresentationTemplateSlide): string {
  if (sourceFileUrl) {
    const fromUrl = deriveNameFromSourceUrl(sourceFileUrl);
    if (fromUrl) return fromUrl;
  }
  return firstSlide.name;
}

/** Most common non-null category among `slides`; ties go to whichever
 *  category was seen first. Null when no slide carries one. */
function dominantCategory(slides: readonly PresentationTemplateSlide[]): string | null {
  const counts = new Map<string, number>();
  const order: string[] = [];
  for (const slide of slides) {
    if (!slide.category) continue;
    if (!counts.has(slide.category)) {
      counts.set(slide.category, 0);
      order.push(slide.category);
    }
    counts.set(slide.category, counts.get(slide.category)! + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const category of order) {
    const count = counts.get(category)!;
    if (count > bestCount) {
      best = category;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Groups a flat entry list into presentations, preserving each group's
 * first-seen order and sorting each group's own slides by `slideIndex` (a
 * `null` slideIndex — an entry with no derivable position — sorts last,
 * after every known-positioned sibling, rather than colliding at 0).
 */
export function groupIntoPresentations(entries: readonly PresentationTemplateSlide[]): PresentationTemplate[] {
  const groups = new Map<string, PresentationTemplateSlide[]>();
  const groupOrder: string[] = [];

  for (const entry of entries) {
    const key = entry.sourceFileUrl ?? `standalone:${entry.id}`;
    let group = groups.get(key);
    if (!group) {
      group = [];
      groups.set(key, group);
      groupOrder.push(key);
    }
    group.push(entry);
  }

  return groupOrder.map((key) => {
    const rawSlides = groups.get(key)!;
    const slides = [...rawSlides].sort((a, b) => {
      if (a.slideIndex === null && b.slideIndex === null) return 0;
      if (a.slideIndex === null) return 1;
      if (b.slideIndex === null) return -1;
      return a.slideIndex - b.slideIndex;
    });
    const firstSlide = slides[0]!;
    const sourceFileUrl = key.startsWith("standalone:") ? null : key;
    return {
      id: key,
      name: deriveGroupName(sourceFileUrl, firstSlide),
      category: dominantCategory(slides),
      thumbnailUrl: firstSlide.thumbnailUrl,
      slides,
    };
  });
}

/**
 * "…/Board_Meeting.pptx?sv=…" -> "…/Board_Meeting.cache?sv=…" — the per-file
 * `.cache` sibling that (unlike the aggregate `presentations.cache`, one
 * representative entry per file) carries every real slide of THIS deck. Same
 * directory, same SAS query string, just the extension swapped, so this needs
 * no fresh blob-sas request. Returns null for a URL that doesn't end in
 * `.pptx` (shouldn't happen for a real `sourceFileUrl`, but a corrupted/
 * stripped one round-tripped through the dialog hand-off is untrusted input).
 */
function perFileCacheUrlFromSourceFileUrl(sourceFileUrl: string): string | null {
  try {
    const url = new URL(sourceFileUrl);
    if (!/\.pptx$/i.test(url.pathname)) return null;
    url.pathname = url.pathname.replace(/\.pptx$/i, ".cache");
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Fetches `sourceFileUrl`'s per-file `.cache` sibling and parses it into the
 * presentation's FULL, real slide list.
 *
 * `presentations.cache` (see `fetchPresentationTemplates`) only ever carries
 * a representative slide (sometimes two) per source `.pptx`, not the whole
 * deck — a 14-slide "Board Meeting" deck still shows up as one entry there.
 * The per-file `.cache` is the same entry shape (`SlideNumber`, `SlideName`,
 * `FilePath`, `Category?`, `Base64ImageData?`) but scoped to just this one
 * file, with one entry per real slide — a small JSON fetch, not a multi-MB
 * `.pptx` download-and-unzip, so this is what "open this presentation" reads
 * instead of ever touching the `.pptx` itself.
 *
 * Returns null (not `[]`) when the file is missing, unreadable, or empty —
 * callers keep whatever (possibly incomplete) slide list they already have
 * in that case rather than replacing it with nothing.
 */
export async function fetchPresentationCacheSlides(
  sourceFileUrl: string,
  signal?: AbortSignal,
): Promise<readonly PresentationTemplateSlide[] | null> {
  const cacheUrl = perFileCacheUrlFromSourceFileUrl(sourceFileUrl);
  if (!cacheUrl) return null;

  let raw: unknown;
  try {
    raw = await downloadCacheJson(cacheUrl, signal);
  } catch (error) {
    logPresentationTemplatesError(
      "presentation-templates-per-file-cache-fetch",
      getErrorMessage(error, CACHE_DOWNLOAD_FAILED_MESSAGE),
    );
    return null;
  }
  if (!Array.isArray(raw)) return null;

  // Every entry in a per-file cache belongs to this same source file — no
  // need to re-derive/re-resolve a URL from each entry's own `FilePath`.
  const resolveSourceFileUrl = () => sourceFileUrl;
  const entries: PresentationTemplateSlide[] = [];
  raw.forEach((item, index) => {
    const slide = normaliseAggregateCacheEntry(item, index, resolveSourceFileUrl);
    if (slide) entries.push(slide);
  });
  if (entries.length === 0) return null;

  entries.sort((a, b) => {
    if (a.slideIndex === null && b.slideIndex === null) return 0;
    if (a.slideIndex === null) return 1;
    if (b.slideIndex === null) return -1;
    return a.slideIndex - b.slideIndex;
  });
  return entries;
}

/** Suffixes a repeated slide id (two `.cache` files that both name a slide
 *  "Board_Meeting.pptx#1" would otherwise collide) rather than dropping the
 *  later one — every slide is still real, it just needs a distinguishable key. */
function dedupeIds(slides: readonly PresentationTemplateSlide[]): PresentationTemplateSlide[] {
  const seen = new Set<string>();
  return slides.map((slide) => {
    if (!seen.has(slide.id)) {
      seen.add(slide.id);
      return slide;
    }
    let suffix = 2;
    while (seen.has(`${slide.id}-${suffix}`)) suffix += 1;
    const dedupedId = `${slide.id}-${suffix}`;
    seen.add(dedupedId);
    logPresentationTemplatesError("presentation-templates-duplicate-id", `Repeated template id "${slide.id}"`);
    return { ...slide, id: dedupedId };
  });
}

/**
 * The SAS token + the folder it actually points at — everything needed to
 * fetch this template library directly, with a plain unauthenticated
 * `fetch()`. Deliberately a plain exported interface (not just an internal
 * type): a SAS query string is self-contained auth, so ANY context holding
 * one of these — the pane, or the Office Dialog window, which has no bearer
 * token and can't call blob-sas itself — can call
 * `fetchPresentationTemplatesFromSasInfo` with it directly. Only the initial
 * mint (`requestDirectorySasInfo`, an authenticated POST) has to happen in
 * the pane.
 */
export interface DirectorySasInfo {
  readonly sasUrl: string;
  /**
   * Container-relative folder path from the blob-sas response's own
   * `blob_path` field — the actual folder this template's files live in.
   *
   * This can be a DIFFERENT (deeper) folder than the SAS token's own signed
   * directory scope: DeckorateAPI mints the token's directory from
   * `PurePosixPath(row.BlobPath).parent`, which is only correct when
   * `BlobPath` points at a manifest FILE (as Standard/Template's seed rows
   * do); Presentation's seed row still has `BlobPath` pointing at the bare
   * "presentations" folder itself, so `.parent` overshoots one level up to
   * "templates" — the SAS ends up scoped to the parent of where the files
   * actually are. `blob_path` here is passed through unmodified from that
   * same row, so it still names the real folder even though the SAS's own
   * path doesn't.
   *
   * This is still fetchable: a directory-scoped SAS's Read permission is
   * recursive to every descendant blob under the signed directory, not only
   * ones that sit exactly at its own level — so building URLs from this
   * folder path (via `directorySasAbsolutePathUrl`) rather than the SAS's
   * own path works without needing that backend seed-data value fixed.
   */
  readonly folderPath: string | null;
}

/** Mints the SAS token — the one step that needs an authenticated
 *  `HttpClient`, so this only ever runs in the pane. */
export async function requestDirectorySasInfo(http: HttpClient, options: HttpRequestOptions = {}): Promise<DirectorySasInfo> {
  const request: BlobSasRequest = {
    client_id: PRESENTATION_TEMPLATES_CLIENT_ID,
    transaction_type: PRESENTATION_TEMPLATES_TRANSACTION_TYPE,
    template_type: PRESENTATION_TEMPLATES_TEMPLATE_TYPE,
  };
  const response = await http.post<RawBlobSasResponse | null>(BLOB_SAS_PATH, request, options);
  const assets = response?.assets;
  const asset = Array.isArray(assets) ? (assets[0] as RawBlobSasAsset | undefined) : undefined;
  if (typeof asset?.sas_url !== "string" || !asset.sas_url) throw new Error(DIRECTORY_REQUEST_FAILED_MESSAGE);
  const folderPath = typeof asset.blob_path === "string" && asset.blob_path ? asset.blob_path : null;
  return { sasUrl: asset.sas_url, folderPath };
}

/** Resolves one filename against the template folder — using the response's
 *  own `folderPath` when available (the reliable path, even when it differs
 *  from the SAS's own signed scope), falling back to swapping the SAS URL's
 *  last path segment only if the response never carried one. */
function resolveTemplateFileUrl(
  { sasUrl, folderPath }: DirectorySasInfo,
  fileName: string,
  onParseError: (message: string) => void,
): string {
  if (folderPath) return directorySasAbsolutePathUrl(sasUrl, `${folderPath}/${fileName}`, onParseError);
  return directorySasFileUrl(sasUrl, fileName, onParseError);
}

async function downloadCacheJson(sasUrl: string, signal: AbortSignal | undefined): Promise<unknown> {
  const res = await fetch(sasUrl, {
    method: "GET",
    // A user-delegation SAS must echo its own `sv` back as x-ms-version or
    // Azure rejects it with a 403 that reads like a permissions problem —
    // same requirement every other SAS request in this feature has.
    headers: sasVersionHeaders(sasUrl, logPresentationTemplatesError, "presentation-templates-sas-version-parse"),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok) {
    const errText = await res.text().catch((error: unknown) => {
      logPresentationTemplatesError(
        "presentation-templates-cache-body-read",
        getErrorMessage(error, "Failed to read cache-file error body"),
      );
      return "";
    });
    throw new Error(`${CACHE_DOWNLOAD_FAILED_MESSAGE} (${res.status} ${errText})`);
  }
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    logPresentationTemplatesError("presentation-templates-cache-parse", getErrorMessage(error, "Cache JSON.parse failed"));
    throw new Error(CACHE_NOT_JSON_MESSAGE);
  }
}

/**
 * Fetches the one known `presentations.cache` manifest and parses it into
 * whole presentations, grouped by source `.pptx`, given an already-minted
 * `sasInfo` — see the module header for why this needs nothing beyond the
 * directory SAS's existing Read permission. Pure network calls only (`fetch`,
 * no `HttpClient`/bearer token), so this is safe to call from EITHER the pane
 * or the Office Dialog window directly — see `DirectorySasInfo`'s doc
 * comment.
 */
export async function fetchPresentationTemplatesFromSasInfo(
  sasInfo: DirectorySasInfo,
  signal?: AbortSignal,
): Promise<PresentationTemplate[]> {
  const resolveSourceFileUrl = (pptxFile: string): string =>
    resolveTemplateFileUrl(sasInfo, pptxFile, (message) =>
      logPresentationTemplatesError("presentation-templates-source-url-rebuild", message),
    );
  const cacheFileUrl = resolveTemplateFileUrl(sasInfo, AGGREGATE_CACHE_FILE_NAME, (message) =>
    logPresentationTemplatesError("presentation-templates-cache-url-rebuild", message),
  );

  const raw = await downloadCacheJson(cacheFileUrl, signal);
  if (!Array.isArray(raw)) {
    logPresentationTemplatesError(
      "presentation-templates-cache-shape",
      `"${AGGREGATE_CACHE_FILE_NAME}" wasn't a JSON array`,
    );
    return [];
  }

  const entries: PresentationTemplateSlide[] = [];
  raw.forEach((item, index) => {
    const slide = normaliseAggregateCacheEntry(item, index, resolveSourceFileUrl);
    if (slide) entries.push(slide);
  });

  const presentations = groupIntoPresentations(dedupeIds(entries));
  return resolveFullSlideLists(presentations, signal);
}

/**
 * Mints a directory SAS (needs an authenticated `http`, so this is the
 * pane-only entry point) and fetches the library through it. The Office
 * Dialog window instead mints via a round-trip to the pane
 * (`requestDirectorySasInfo` is what the pane calls) and then calls
 * `fetchPresentationTemplatesFromSasInfo` itself directly — see
 * asset-library/hooks/useDialogPresentationTemplates.ts.
 */
export async function fetchPresentationTemplates(
  http: HttpClient,
  options: HttpRequestOptions = {},
): Promise<PresentationTemplate[]> {
  const sasInfo = await requestDirectorySasInfo(http, options);
  return fetchPresentationTemplatesFromSasInfo(sasInfo, options.signal);
}

/**
 * Replaces every presentation's slide list with its real, full one, read from
 * its per-file `.cache` sibling — `presentations.cache` only ever describes
 * one representative slide per file (so every card would otherwise show
 * "1 slide" until individually opened). Done here, eagerly, for every
 * presentation in parallel: unlike a `.pptx` (multi-MB, needs unzipping),
 * each per-file `.cache` is a small JSON fetch, so paying this cost once up
 * front — rather than lazily per-open — is what makes both the grid's slide
 * counts accurate immediately AND opening a presentation instant.
 *
 * A presentation whose per-file cache is missing or unreadable just keeps
 * its original (single-slide) entry rather than the whole library failing.
 */
async function resolveFullSlideLists(
  presentations: readonly PresentationTemplate[],
  signal: AbortSignal | undefined,
): Promise<PresentationTemplate[]> {
  return Promise.all(
    presentations.map(async (presentation) => {
      const first = presentation.slides[0];
      if (!first?.sourceFileUrl) return presentation;
      const slides = await fetchPresentationCacheSlides(first.sourceFileUrl, signal);
      return slides ? { ...presentation, slides } : presentation;
    }),
  );
}

/** Notice for a presentation template whose source file is gone from storage. */
export const PRESENTATION_TEMPLATE_REMOVED_COPY = {
  title: "That presentation template is no longer available",
  message: "Your organisation updated the library, so nothing was inserted. Choose another one instead.",
} as const;

/** The in-pane confirmation after a successful Presentation Templates insert. */
export function presentationTemplatesAddedMessage(count: number): string {
  return count === 1
    ? "1 slide added after your current slide."
    : `${count} slides added after your current slide.`;
}
